/**
 * The member's own rack, with editing where the active organization has the
 * premium ribbon-designer (docs/Decisions.md D42): reorder with Up and Down
 * buttons (keyboard accessible, no drag needed) and hide items from others
 * in this organization's context. Also shows progress toward every pre-built
 * platform ribbon.
 */
import { useEffect, useState } from 'react';
import { ui, cn } from '../../lib/ui';
import {
  moveItem,
  arrangementFrom,
  ribbonLabel,
  scopeLabel,
  METRIC_LABELS,
} from '../../lib/ribbons';
import * as ribbonsApi from '../../api/ribbons';
import { ErrorAlert } from '../ui';
import { useToast } from '../toast';
import RibbonBar from './RibbonBar';
import BadgeMedallion from './BadgeMedallion';
import { RackDisplay } from './RibbonRack';
import { PremiumNotice, MoveButtons } from './parts';

function Progress() {
  const [progress, setProgress] = useState(null);
  useEffect(() => {
    ribbonsApi
      .getMyProgress()
      .then(setProgress)
      .catch(() => setProgress(null));
  }, []);
  if (!progress) return null;
  return (
    <section className={ui.card} aria-labelledby="ribbon-progress-heading">
      <h3 id="ribbon-progress-heading" className={ui.h3}>
        Platform ribbons
      </h3>
      <p className={cn(ui.muted, 'mb-4 text-body-s')}>
        Available on every plan, earned in any organization, visible platform wide.
      </p>
      <div className={ui.tableWrap}>
        <table className={ui.table}>
          <thead>
            <tr>
              <th scope="col">Ribbon</th>
              <th scope="col">Activity</th>
              <th scope="col">Progress</th>
            </tr>
          </thead>
          <tbody>
            {progress.platformRibbons.map(r => (
              <tr key={r.ribbonId}>
                <td>{r.name}</td>
                <td>{METRIC_LABELS[r.metric] || r.metric}</td>
                <td>
                  {r.earned ? (
                    <span className={ui.pillSuccess}>Earned</span>
                  ) : (
                    <span className="tabular-nums">
                      {Math.min(r.count, r.threshold)} of {r.threshold}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export default function RackEditor({ isOrgAdmin }) {
  const [rack, setRack] = useState(null);
  const [items, setItems] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const toast = useToast();

  function load() {
    ribbonsApi
      .getMyRack()
      .then(data => {
        setRack(data);
        setItems([...(data.ribbons || []), ...(data.badges || [])]);
        setDirty(false);
      })
      .catch(setError);
  }

  useEffect(load, []);

  function change(next) {
    setItems(next);
    setDirty(true);
  }

  async function save() {
    setBusy(true);
    setError(null);
    try {
      await ribbonsApi.arrangeMyRack(arrangementFrom(items));
      toast.success('Rack saved');
      load();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  if (error && !rack) return <ErrorAlert error={error} />;
  if (!rack) {
    return (
      <p className={ui.muted} role="status">
        Loading your rack
      </p>
    );
  }

  const canArrange = rack.context && rack.context.canArrange;
  const preview = {
    ...rack,
    ribbons: items.filter(i => i.kind === 'ribbon'),
    badges: items.filter(i => i.kind === 'badge'),
  };

  return (
    <div className="flex flex-col gap-6">
      <section className={ui.card} aria-labelledby="my-rack-heading" data-testid="ribbon-rack">
        <h3 id="my-rack-heading" className={ui.h3}>
          Your rack
        </h3>
        <p className={cn(ui.muted, 'mb-4 text-body-s')}>
          Ribbons and badges from every organization you belong to, on your platform-wide profile.
        </p>
        <RackDisplay rack={preview} showScope />
      </section>

      {canArrange ? (
        <section className={ui.card} aria-labelledby="arrange-heading">
          <h3 id="arrange-heading" className={ui.h3}>
            Arrange your rack in this organization
          </h3>
          <ErrorAlert error={error} />
          {items.length === 0 ? (
            <p className={ui.muted}>Nothing to arrange yet.</p>
          ) : (
            <ol className="mb-4 flex flex-col gap-2">
              {items.map((item, index) => (
                <li
                  key={item.awardId}
                  className={cn(ui.row, 'rounded-dcon-md border border-z-line px-3 py-2')}
                >
                  {item.kind === 'badge' ? (
                    <BadgeMedallion item={item} className="size-8!" />
                  ) : (
                    <RibbonBar item={item} />
                  )}
                  <span className="flex-1">
                    <span className="block text-body-s font-semibold text-dcon-ink">
                      {ribbonLabel(item)}
                    </span>
                    <span className="block text-caption text-dcon-ink-muted">
                      {scopeLabel(item.visibility)}
                    </span>
                  </span>
                  <label className="inline-flex items-center gap-2 text-body-s">
                    <input
                      type="checkbox"
                      className={ui.checkbox}
                      checked={Boolean(item.hidden)}
                      onChange={e =>
                        change(
                          items.map(i =>
                            i.awardId === item.awardId ? { ...i, hidden: e.target.checked } : i
                          )
                        )
                      }
                    />
                    Hide from others
                  </label>
                  <MoveButtons
                    label={ribbonLabel(item)}
                    index={index}
                    count={items.length}
                    onMove={(i, d) => change(moveItem(items, i, d))}
                  />
                </li>
              ))}
            </ol>
          )}
          <button type="button" className={ui.btnPrimary} onClick={save} disabled={busy || !dirty}>
            {busy ? 'Saving' : 'Save arrangement'}
          </button>
        </section>
      ) : rack.context && rack.context.orgId && !rack.context.ribbonDesigner ? (
        <PremiumNotice isOrgAdmin={isOrgAdmin} />
      ) : (
        <p className={cn(ui.muted, 'text-body-s')}>
          Your rack uses the default order: platform ribbons by precedence, then by the date you
          earned them.
        </p>
      )}

      <Progress />
    </div>
  );
}
